import { useCallback, useEffect, useRef, useState } from 'react';
import { trpClient } from '../client/trpClient';
import { QuoteRecommendationResult, QuoteRecommendationRun } from '../types/quote.types';

export const RUN_POLL_MS = 3000;
/** Fallback for callers that do not know the supplier count. */
export const RUN_LONG_AFTER_MS = 60000;

const GENERATE_FAILED = "I couldn't finish the award recommendation. Try again — your quotes are untouched.";

type HttpError = { message?: string; response?: { data?: { error?: string | { message?: string } } } };

const errorMessage = (e: unknown, fallback: string): string => {
    const err = e as HttpError;
    const body = err?.response?.data?.error;
    return (typeof body === 'string' ? body : body?.message) || err?.message || fallback;
};

/**
 * Loads the stored recommendation and owns the one AI run that builds it.
 * The server keeps a run marker, so a refresh mid-run resumes polling instead
 * of starting a second generation, and a 202 from POST means "already running".
 */
export const useQuoteRecommendationRun = (
    rfqId?: string,
    /** Past this, counted from the server's start time, the overlay says it is taking a bit longer. */
    longAfterMs: number = RUN_LONG_AFTER_MS,
) => {
    const [result, setResult] = useState<QuoteRecommendationResult | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isLong, setIsLong] = useState(false);
    const [runError, setRunError] = useState<string | null>(null);
    const mounted = useRef(true);
    const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longAfter = useRef(longAfterMs);
    longAfter.current = longAfterMs;

    const stopTimers = () => {
        if (pollTimer.current) clearTimeout(pollTimer.current);
        if (longTimer.current) clearTimeout(longTimer.current);
        pollTimer.current = null;
        longTimer.current = null;
    };

    const finish = useCallback((data: QuoteRecommendationResult | null, error: string | null) => {
        stopTimers();
        if (!mounted.current) return;
        if (data) setResult(data);
        setRunError(error);
        setIsGenerating(false);
        setIsLong(false);
    }, []);

    const startWaiting = useCallback((runningSince?: string) => {
        setIsGenerating(true);
        setRunError(null);
        const started = runningSince ? new Date(runningSince).getTime() : Date.now();
        const untilLong = Math.max(0, started + longAfter.current - Date.now());
        if (longTimer.current) clearTimeout(longTimer.current);
        setIsLong(untilLong === 0);
        longTimer.current = setTimeout(() => mounted.current && setIsLong(true), untilLong);
    }, []);

    const settle = useCallback((data: QuoteRecommendationResult | null, run: QuoteRecommendationRun | null) => {
        if (run?.status === 'FAILED') finish(null, run.error?.message || GENERATE_FAILED);
        else finish(data, null);
    }, [finish]);

    const poll = useCallback((id: string) => {
        pollTimer.current = setTimeout(async () => {
            try {
                const { data, run } = await trpClient.getQuoteRecommendation(id);
                if (!mounted.current) return;
                if (run?.status === 'RUNNING') poll(id);
                else settle(data, run);
            } catch (e: unknown) {
                finish(null, errorMessage(e, GENERATE_FAILED));
            }
        }, RUN_POLL_MS);
    }, [finish, settle]);

    useEffect(() => {
        mounted.current = true;
        if (!rfqId) {
            setIsLoading(false);
            return undefined;
        }
        setIsLoading(true);
        trpClient.getQuoteRecommendation(rfqId)
            .then(({ data, run }) => {
                if (!mounted.current) return;
                setResult(data);
                if (run?.status === 'RUNNING') {
                    startWaiting(run.runningSince);
                    poll(rfqId);
                }
            })
            // A failed read leaves the step on its empty state; Generate is still there.
            .catch(() => undefined)
            .finally(() => mounted.current && setIsLoading(false));
        return () => {
            mounted.current = false;
            stopTimers();
        };
    }, [rfqId, poll, startWaiting]);

    const generate = useCallback(async () => {
        if (!rfqId || isGenerating) return;
        startWaiting();
        try {
            const { data, run } = await trpClient.generateQuoteRecommendation(rfqId);
            if (!mounted.current) return;
            if (!data && run?.status === 'RUNNING') {
                startWaiting(run.runningSince);
                poll(rfqId);
            } else {
                settle(data, run);
            }
        } catch (e: unknown) {
            finish(null, errorMessage(e, GENERATE_FAILED));
        }
    }, [rfqId, isGenerating, startWaiting, poll, settle, finish]);

    return {
        result,
        setResult,
        isLoading,
        isGenerating,
        isLong,
        runError,
        dismissRunError: () => setRunError(null),
        generate,
    };
};

export default useQuoteRecommendationRun;
